// Chronique du monde : titre et accroche, acte en cours, menace et horloges, quêtes,
// chapitres du Conteur et mémorial des héros tombés.
import type { ComponentChildren } from "preact";
import type { Chapter, Clock, ClientView, Millis, Quest } from "../../shared/types";
import "./Chronicle.css";

export interface ChronicleProps { view: ClientView }

type World = ClientView["world"];
type Threat = NonNullable<World["threat"]>;
type Fallen = ClientView["graveyard"][number];

const PARIS = "Europe/Paris";
const dayFmt = new Intl.DateTimeFormat("fr-FR", { timeZone: PARIS, day: "numeric", month: "long" });
const dayYearFmt = new Intl.DateTimeFormat("fr-FR", { timeZone: PARIS, day: "numeric", month: "long", year: "numeric" });
const yearFmt = new Intl.DateTimeFormat("fr-FR", { timeZone: PARIS, year: "numeric" });
const dayKeyFmt = new Intl.DateTimeFormat("fr-CA", { timeZone: PARIS, year: "numeric", month: "2-digit", day: "2-digit" });

const QUEST_ORDER: Record<Quest["status"], number> = { active: 0, terminee: 1, echouee: 2 };
const QUEST_LABEL: Record<Quest["status"], string> = { active: "En cours", terminee: "Accomplie", echouee: "Échouée" };
const ROMAN: [number, string][] = [[10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"]];
const DIAL_C = 60;
const DIAL_R = 52;

/** « 12 octobre », avec l’année si elle n’est pas celle de maintenant. */
function formatDay(at: Millis, now: Millis): string {
  return (yearFmt.format(at) === yearFmt.format(now) ? dayFmt : dayYearFmt).format(at);
}

/** « 12 octobre » pour un jour, « 12 octobre – 14 octobre » pour une période. */
function formatPeriod(from: Millis, to: Millis, now: Millis): string {
  if (dayKeyFmt.format(from) === dayKeyFmt.format(to)) return formatDay(from, now);
  return `${formatDay(from, now)} – ${formatDay(to, now)}`;
}

function isoOf(at: Millis): string | undefined {
  return Number.isFinite(at) ? new Date(at).toISOString() : undefined;
}

function toRoman(n: number): string {
  if (!Number.isFinite(n) || n < 1) return "";
  if (n > 10) return String(n);
  let rest = Math.floor(n);
  let out = "";
  for (const [value, symbol] of ROMAN) {
    while (rest >= value) { out += symbol; rest -= value; }
  }
  return out;
}

/** « a », « a et b », « a, b et c ». */
function joinFr(items: string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} et ${items[items.length - 1]}`;
}

function normalizeName(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

function dialState(filled: number, segments: number): { done: number; total: number } {
  const total = Number.isFinite(segments) ? Math.max(1, Math.round(segments)) : 1;
  const done = Number.isFinite(filled) ? Math.min(total, Math.max(0, Math.round(filled))) : 0;
  return { done, total };
}

/** Secteur n (0 = premier, au sommet) d'un cadran de `total` secteurs, sens horaire. */
function segmentPath(index: number, total: number): string {
  const step = 360 / total;
  const start = -90 + index * step;
  const point = (deg: number): [number, number] => {
    const rad = (deg * Math.PI) / 180;
    return [DIAL_C + DIAL_R * Math.cos(rad), DIAL_C + DIAL_R * Math.sin(rad)];
  };
  const [x0, y0] = point(start);
  const [x1, y1] = point(start + step);
  const large = step > 180 ? 1 : 0;
  return `M${DIAL_C} ${DIAL_C} L${x0.toFixed(2)} ${y0.toFixed(2)} A${DIAL_R} ${DIAL_R} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)} Z`;
}

function worldStatusLine(status: World["status"], message: string | null): string | null {
  if (status === "pret") return null;
  if (status === "erreur") return message || "Le Conteur a rencontré un problème.";
  return message || "Le Conteur tisse le monde…";
}

/** Noms des héros connus de cette vue (mon personnage et les autres). */
function heroNameResolver(view: ClientView): (id: string) => string | null {
  const known = new Map<string, string>();
  for (const other of view.others) known.set(other.id, other.name);
  if (view.character) known.set(view.character.id, view.character.name);
  return (id) => known.get(id) ?? null;
}

function partyLine(quest: Quest, nameOf: (id: string) => string | null): string | null {
  const count = quest.characterIds.length;
  if (count === 0) return null;
  const names = quest.characterIds.map((id) => nameOf(id)).filter((n): n is string => n !== null);
  if (names.length === 0) return count > 1 ? `${count} héros engagés` : "1 héros engagé";
  const others = count - names.length;
  const parts = others > 0 ? [...names, others > 1 ? `${others} autres héros` : "1 autre héros"] : names;
  return `Avec ${joinFr(parts)}`;
}

function Empty({ children }: { children: ComponentChildren }) {
  return <p class="chronicle-empty">{children}</p>;
}

function SectionHead({ id, title, count, aside }: { id: string; title: string; count?: number; aside?: ComponentChildren }) {
  return (
    <div class="chronicle-section-head">
      <h3 id={id} class="chronicle-section-title">{title}</h3>
      {aside}
      {count !== undefined && count > 0 && <span class="chip">{count}</span>}
    </div>
  );
}

/** Horloge façon Blades in the Dark : un disque découpé en secteurs, remplis en braise. */
function Dial({ filled, segments, label, mini = false }: { filled: number; segments: number; label: string; mini?: boolean }) {
  const { done, total } = dialState(filled, segments);
  const filledClass = "chronicle-dial-seg chronicle-dial-seg--filled";
  return (
    <svg
      class={mini ? "chronicle-dial chronicle-dial--mini" : "chronicle-dial"}
      viewBox="0 0 120 120"
      role="img"
      aria-label={`${label} : ${done} sur ${total} segments remplis`}
    >
      {total === 1
        ? <circle class={done > 0 ? filledClass : "chronicle-dial-seg"} cx={DIAL_C} cy={DIAL_C} r={DIAL_R} />
        : Array.from({ length: total }, (_, i) => (
          <path key={i} class={i < done ? filledClass : "chronicle-dial-seg"} d={segmentPath(i, total)} />
        ))}
      <circle class="chronicle-dial-rim" cx={DIAL_C} cy={DIAL_C} r={DIAL_R} />
      <circle class="chronicle-dial-hub" cx={DIAL_C} cy={DIAL_C} r={5} />
    </svg>
  );
}

function ThreatBlock({ threat }: { threat: Threat }) {
  const { done, total } = dialState(threat.filled, threat.segments);
  return (
    <div class="chronicle-threat">
      <Dial filled={done} segments={total} label={`Menace, ${threat.name}`} />
      <div class="chronicle-threat-text">
        <p class="chronicle-kicker">La menace</p>
        <p class="chronicle-threat-name">{threat.name}</p>
        <p class="chronicle-threat-count">{done} sur {total} segments remplis</p>
        <p class="chronicle-threat-note">Chaque segment rempli rapproche le péril.</p>
      </div>
    </div>
  );
}

function ClockCard({ clock }: { clock: Clock }) {
  return (
    <li class="chronicle-clock">
      <Dial filled={clock.filled} segments={clock.segments} label={clock.name} mini />
      <p class="chronicle-clock-name">{clock.name}</p>
      {clock.description && <p class="chronicle-clock-desc">{clock.description}</p>}
    </li>
  );
}

function QuestCard({ quest, nameOf }: { quest: Quest; nameOf: (id: string) => string | null }) {
  const total = quest.objectives.length;
  const done = quest.objectives.filter((o) => o.done).length;
  const { xp, gold, itemKeys } = quest.reward;
  const rewards = [
    xp > 0 ? `+${xp} XP` : null,
    gold > 0 ? `${gold} or` : null,
    itemKeys.length > 0 ? `${itemKeys.length} ${itemKeys.length > 1 ? "objets" : "objet"}` : null,
  ].filter((r): r is string => r !== null);
  const party = partyLine(quest, nameOf);
  return (
    <li class="chronicle-quest">
      <div class="chronicle-quest-head">
        <h4 class="chronicle-quest-title">{quest.title}</h4>
        <span class={`chronicle-badge chronicle-badge--${quest.status}`}>{QUEST_LABEL[quest.status]}</span>
      </div>
      {quest.description && <p class="chronicle-quest-desc">{quest.description}</p>}
      {total > 0 && (
        <>
          <ul class="chronicle-objectives" aria-label="Objectifs">
            {quest.objectives.map((o, i) => (
              <li key={i} class={o.done ? "chronicle-objective chronicle-objective--done" : "chronicle-objective"}>
                <span class="chronicle-check" aria-hidden="true" />
                <span class="chronicle-objective-text">
                  {o.text}
                  {o.done && <span class="sr-only"> (accompli)</span>}
                </span>
              </li>
            ))}
          </ul>
          <div
            class="chronicle-progress"
            role="progressbar"
            aria-label="Objectifs accomplis"
            aria-valuemin={0}
            aria-valuemax={total}
            aria-valuenow={done}
          >
            <span class="chronicle-progress-fill" style={{ width: `${Math.round((done / total) * 100)}%` }} />
          </div>
        </>
      )}
      {(rewards.length > 0 || party) && (
        <div class="chronicle-quest-foot">
          {rewards.length > 0 && (
            <div class="chronicle-rewards">
              <span class="chronicle-rewards-label">Récompense</span>
              {rewards.map((r, i) => <span key={i} class="chronicle-reward">{r}</span>)}
            </div>
          )}
          {party && <p class="chronicle-party">{party}</p>}
        </div>
      )}
    </li>
  );
}

/** Frise des actes : un point par acte, plein pour les actes passés, lumineux pour l’acte en cours. */
function Frieze({ index, total }: { index: number; total: number }) {
  return (
    <ol class="chronicle-frieze" aria-label={`Acte ${index + 1} sur ${total}`}>
      {Array.from({ length: total }, (_, i) => {
        const state = i < index ? "done" : i === index ? "current" : "next";
        return (
          <li
            key={i}
            class={`chronicle-frieze-step chronicle-frieze-step--${state}`}
            aria-current={state === "current" ? "step" : undefined}
          >
            <span class="sr-only">Acte {toRoman(i + 1)}</span>
          </li>
        );
      })}
    </ol>
  );
}

function ChapterList({ chapters, now }: { chapters: Chapter[]; now: Millis }) {
  const chronological = [...chapters].sort((a, b) => a.from - b.from || a.to - b.to);
  return (
    <ol class="chronicle-chapters">
      {[...chronological].reverse().map((ch, i) => (
        <li key={ch.id} class={i === 0 ? "chronicle-chapter chronicle-chapter--latest" : "chronicle-chapter"}>
          <p class="chronicle-chapter-meta">
            Chapitre {chronological.length - i} · <time dateTime={isoOf(ch.to)}>{formatPeriod(ch.from, ch.to, now)}</time>
          </p>
          <h4 class="chronicle-chapter-title">{ch.title}</h4>
          <p class="chronicle-chapter-summary">{ch.summary}</p>
        </li>
      ))}
    </ol>
  );
}

function Memorial({ fallen, now }: { fallen: Fallen[]; now: Millis }) {
  const recent = [...fallen].sort((a, b) => (b.deathAt ?? 0) - (a.deathAt ?? 0));
  return (
    <ul class="chronicle-memorial-list">
      {recent.map((f, i) => (
        <li key={i} class="chronicle-stone">
          <span class="chronicle-stone-name">{f.name}</span>
          <span class="chronicle-stone-meta">{f.className}, niveau {f.level}</span>
          <span class="chronicle-stone-date">
            {typeof f.deathAt === "number" ? `Tombé(e) le ${formatDay(f.deathAt, now)}` : "Date inconnue"}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Bougie décorative ; sa flamme vacille (le mouvement est coupé si l’utilisateur le demande). */
function Candle() {
  return (
    <svg class="chronicle-candle" viewBox="0 0 40 64" aria-hidden="true">
      <ellipse class="chronicle-candle-glow" cx="20" cy="22" rx="17" ry="19" />
      <rect class="chronicle-candle-wax" x="14" y="30" width="12" height="28" rx="2" />
      <line class="chronicle-candle-wick" x1="20" y1="26" x2="20" y2="30" />
      <path class="chronicle-candle-flame" d="M20 2 C24 9 28 14 28 19 C28 23 24 26 20 26 C16 26 12 23 12 19 C12 14 16 9 20 2 Z" />
      <path class="chronicle-candle-core" d="M20 13 C22 16 23 18 23 21 C23 23.5 21.5 24.5 20 24.5 C18.5 24.5 17 23.5 17 21 C17 18 18 16 20 13 Z" />
    </svg>
  );
}

export function Chronicle(props: ChronicleProps) {
  const { view } = props;
  const { world, now } = view;
  const { threat, act } = world;
  const title = world.title?.trim() || world.name;
  const tagline = world.tagline?.trim() || null;
  const status = worldStatusLine(world.status, world.statusMessage);
  const nameOf = heroNameResolver(view);
  const quests = [...view.quests].sort(
    (a, b) => QUEST_ORDER[a.status] - QUEST_ORDER[b.status] || b.createdAt - a.createdAt,
  );
  // L’horloge de la menace figure déjà en tête de section : on ne la répète pas dans la liste.
  const threatKey = threat ? normalizeName(threat.name) : null;
  const clocks = view.clocks.filter((c) => c.visible && normalizeName(c.name) !== threatKey);
  const heroes = world.playerCount;

  return (
    <article class="chronicle">
      <header class="chronicle-hero">
        <p class="chronicle-eyebrow">Chronique du monde</p>
        <h2 class="chronicle-title">{title}</h2>
        {tagline && <p class="chronicle-tagline">{tagline}</p>}
        <p class="chronicle-meta">
          <span class="chip">{heroes > 0 ? `${heroes} héros` : "Pas encore de héros"}</span>
        </p>
        {status && (
          <p
            class={world.status === "erreur" ? "chronicle-world-status chronicle-world-status--error" : "chronicle-world-status"}
            role="status"
          >
            {world.status === "genese" && <span class="chronicle-pulse" aria-hidden="true" />}
            {status}
          </p>
        )}
      </header>
      <div class="chronicle-rule" aria-hidden="true" />

      <section class="card chronicle-card" aria-labelledby="chronicle-acte">
        <SectionHead id="chronicle-acte" title="Acte en cours" />
        {act ? (
          <>
            <p class="chronicle-act-title">
              Acte {toRoman(act.index + 1)}
              {act.title ? ` — ${act.title}` : ""}
            </p>
            {act.total > 0 && <Frieze index={act.index} total={act.total} />}
          </>
        ) : (
          <Empty>Le premier acte n’est pas encore ouvert. Le Conteur prépare le terrain.</Empty>
        )}
      </section>

      <section class="card chronicle-card" aria-labelledby="chronicle-menace">
        <SectionHead id="chronicle-menace" title="Menace et horloges" />
        {threat ? (
          <ThreatBlock threat={threat} />
        ) : (
          <Empty>La menace n’a pas encore de visage. Elle se révélera au fil des veillées.</Empty>
        )}
        {clocks.length > 0 ? (
          <ul class="chronicle-clocks" aria-label="Autres horloges">
            {clocks.map((c) => <ClockCard key={c.id} clock={c} />)}
          </ul>
        ) : (
          threat && <Empty>Aucune autre horloge ne tourne dans l’ombre pour l’instant.</Empty>
        )}
      </section>

      <section class="card chronicle-card" aria-labelledby="chronicle-quetes">
        <SectionHead id="chronicle-quetes" title="Quêtes" count={quests.length} />
        {quests.length > 0 ? (
          <ul class="chronicle-quests">
            {quests.map((q) => <QuestCard key={q.id} quest={q} nameOf={nameOf} />)}
          </ul>
        ) : (
          <Empty>Aucune quête n’est encore ouverte. Une rumeur, une promesse ou un cri dans la nuit suffiront.</Empty>
        )}
      </section>

      <section class="card chronicle-card" aria-labelledby="chronicle-chapitres">
        <SectionHead id="chronicle-chapitres" title="Chapitres" count={view.chapters.length} />
        {view.chapters.length > 0 ? (
          <ChapterList chapters={view.chapters} now={now} />
        ) : (
          <Empty>Le Conteur n’a pas encore écrit de chapitre. La première chronique paraîtra au petit matin, après huit heures.</Empty>
        )}
      </section>

      <section class="card chronicle-card chronicle-memorial" aria-labelledby="chronicle-tombes">
        <SectionHead id="chronicle-tombes" title="Ils sont tombés" aside={<Candle />} />
        {view.graveyard.length > 0 ? (
          <Memorial fallen={view.graveyard} now={now} />
        ) : (
          <Empty>Personne n’est encore tombé. Que vos flammes restent allumées.</Empty>
        )}
      </section>
    </article>
  );
}
